import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { IUpdateEvent } from '../../../models/layout-config/layout-configuration.model';
import { SelectionResults } from '../../../models/selection-results/selection-results.model';
import { ValidSize } from '../../../models/selection-results/valid-size.model';
import { BaseGridComponent } from '../../base-grid/base-grid.component';
import { ColumnList } from '../../constants/column-list.const';

@Component({
  selector: 'inline-fans-grid',
  templateUrl: '../../base-grid/base-grid.component.html',
  styleUrls: ['../../base-grid/base-grid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class InlineFansGridComponent
  extends BaseGridComponent
  implements OnInit, OnDestroy
{
  private selectionResultsSubscription: Subscription;

  constructor(protected injector: Injector) {
    super(injector);

    this.gridVersion = '1';

    this.footerProductType = 'Fan(s)';
  }

  ngOnInit() {
    this.columnList = ColumnList.getColumns(
      'actualCFM',
      'externalSP',
      'relativeCost',
      'budgetPrice',
      'operatingCost',
      'bhp',
      'inletSones',
      'inletDba',
      'fanRPM',
      'fanSeries',
      'driveType',
      'weight',
      'amca',
      'watts',
      'wheelDiameter',
      'motorTechnology',
      'motorHP',
      'outletVelocity',
      'staticEfficiency',
      'fei',
      'feiFactorsInputPower',
      'totalEfficiency',
      'peakTotalEfficiency',
      'pointsWithinPeakTotalEfficiency',
      'hasVFD',
      'operatingFrequency',
      'speedNonAdjusted',
      'hasSpeedController',
      'hasBaffle',
      'startupPower',
      'speedMax',
      'tipSpeed',
      'inletOctaveBand63Hz',
      'inletOctaveBand125Hz',
      'inletOctaveBand250Hz',
      'inletOctaveBand500Hz',
      'inletOctaveBand1kHz',
      'inletOctaveBand2kHz',
      'inletOctaveBand4kHz',
      'inletOctaveBand8kHz',
      'inletLwa',
      'outletOctaveBand63Hz',
      'outletOctaveBand125Hz',
      'outletOctaveBand250Hz',
      'outletOctaveBand500Hz',
      'outletOctaveBand1kHz',
      'outletOctaveBand2kHz',
      'outletOctaveBand4kHz',
      'outletOctaveBand8kHz',
      'outletLwa',
      'outletSones',
      'outletDba',
      'radiatedOctaveBand63Hz',
      'radiatedOctaveBand125Hz',
      'radiatedOctaveBand250Hz',
      'radiatedOctaveBand500Hz',
      'radiatedOctaveBand1kHz',
      'radiatedOctaveBand2kHz',
      'radiatedOctaveBand4kHz',
      'radiatedOctaveBand8kHz',
      'radiatedLwa',
      'radiatedSones',
      'radiatedDba',
      'driveLossPercentage',
    );

    this.setVisibleColumns([
      'actualCFM',
      'externalSP',
      'relativeCost',
      'operatingCost',
      'motorTechnology',
      'bhp',
      'fei',
      'inletSones',
      'inletDba',
      'fanRPM',
      'driveType',
      'weight',
      'fanSeries',
      'amca',
    ]);

    this.selectionResultsSubscription = this.selectionResultsReceived.subscribe(
      (selectionResults: SelectionResults) => {
        const newData = [];

        selectionResults.validSizes.forEach((size) => {
          newData.push(this.validSizeToDSRow(size));
        });

        this.dataSource.data = newData;
      },
    );

    this.updateColumnHelp();
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.selectionResultsSubscription.unsubscribe();
  }

  private validSizeToDSRow(size: ValidSize) {
    const rowData = {
      rank: size.sorting.recommendedOrder,
      name: size.name,
      actualCFM: size.outputs.operatingVolume,
      externalSP: size.outputs.operatingStaticPressure,
      operatingCost: size.outputs.operatingCost,
      relativeCost: size.outputs.relativeCost,
      budgetPrice: size.pricing.price,
      bhp: size.outputs.operatingPower,
      inletSones: size.outputs.inletSound.sones,
      inletDba: size.outputs.inletSound.dba,
      fanRPM: size.outputs.speed,
      fanSeries: size.outputs.constructionType,
      driveType: size.outputs.driveType,
      weight: size.pricing.weight,
      driveLossPercentage: size.outputs.driveLossPercentage,
      amca: size.amcaRatings,
      watts: size.outputs.watts,
      wheelDiameter: size.outputs.wheelDiameter,
      motorTechnology: this.getFanMotorTechnology(size),
      motorHP: size.outputs.minimumMotorSizeNema,
      outletVelocity: size.outputs.outletVelocity,
      staticEfficiency: size.outputs.staticEfficiency,
      feg: size.outputs.feg,
      fei: size.outputs.fei,
      feiFactorsInputPower: size.outputs.feiFactorsInputPower,
      totalEfficiency: size.outputs.totalEfficiency,
      peakTotalEfficiency: size.outputs.peakTotalEfficiency,
      pointsWithinPeakTotalEfficiency:
        size.outputs.pointsWithinPeakTotalEfficiency,
      hasVFD: size.outputs.hasVFD,
      operatingFrequency: size.outputs.operatingFrequency,
      speedNonAdjusted: size.outputs.speedNonAdjusted,
      hasSpeedController: size.outputs.hasSpeedController,
      hasBaffle: size.outputs.hasBaffle,
      startupPower: size.outputs.startupPower,
      speedMax: size.outputs.speedMax,
      tipSpeed: size.outputs.tipSpeed,
      inletOctaveBand63Hz: size.outputs.inletSound.octaveBand63Hz,
      inletOctaveBand125Hz: size.outputs.inletSound.octaveBand125Hz,
      inletOctaveBand250Hz: size.outputs.inletSound.octaveBand250Hz,
      inletOctaveBand500Hz: size.outputs.inletSound.octaveBand500Hz,
      inletOctaveBand1kHz: size.outputs.inletSound.octaveBand1kHz,
      inletOctaveBand2kHz: size.outputs.inletSound.octaveBand2kHz,
      inletOctaveBand4kHz: size.outputs.inletSound.octaveBand4kHz,
      inletOctaveBand8kHz: size.outputs.inletSound.octaveBand8kHz,
      inletLwa: size.outputs.inletSound.lwa,
      stable: size.stability.stable,
      sizeData: size,
    };

    if (!!size.outputs.outletSound) {
      rowData['outletOctaveBand63Hz'] = size.outputs.outletSound.octaveBand63Hz;
      rowData['outletOctaveBand125Hz'] =
        size.outputs.outletSound.octaveBand125Hz;
      rowData['outletOctaveBand250Hz'] =
        size.outputs.outletSound.octaveBand250Hz;
      rowData['outletOctaveBand500Hz'] =
        size.outputs.outletSound.octaveBand500Hz;
      rowData['outletOctaveBand1kHz'] = size.outputs.outletSound.octaveBand1kHz;
      rowData['outletOctaveBand2kHz'] = size.outputs.outletSound.octaveBand2kHz;
      rowData['outletOctaveBand4kHz'] = size.outputs.outletSound.octaveBand4kHz;
      rowData['outletOctaveBand8kHz'] = size.outputs.outletSound.octaveBand8kHz;
      rowData['outletSones'] = size.outputs.outletSound.sones;
      rowData['outletDba'] = size.outputs.outletSound.dba;
      rowData['outletLwa'] = size.outputs.outletSound.lwa;
    }

    if (!!size.outputs.radiatedSound) {
      rowData['radiatedOctaveBand63Hz'] =
        size.outputs.radiatedSound.octaveBand63Hz;
      rowData['radiatedOctaveBand125Hz'] =
        size.outputs.radiatedSound.octaveBand125Hz;
      rowData['radiatedOctaveBand250Hz'] =
        size.outputs.radiatedSound.octaveBand250Hz;
      rowData['radiatedOctaveBand500Hz'] =
        size.outputs.radiatedSound.octaveBand500Hz;
      rowData['radiatedOctaveBand1kHz'] =
        size.outputs.radiatedSound.octaveBand1kHz;
      rowData['radiatedOctaveBand2kHz'] =
        size.outputs.radiatedSound.octaveBand2kHz;
      rowData['radiatedOctaveBand4kHz'] =
        size.outputs.radiatedSound.octaveBand4kHz;
      rowData['radiatedOctaveBand8kHz'] =
        size.outputs.radiatedSound.octaveBand8kHz;
      rowData['radiatedSones'] = size.outputs.radiatedSound.sones;
      rowData['radiatedDba'] = size.outputs.radiatedSound.dba;
      rowData['radiatedLwa'] = size.outputs.radiatedSound.lwa;
    }

    return rowData;
  }

  protected override layoutConfigUpdate = (updates?: IUpdateEvent[]) => {
    if (!!this._layoutConfig) {
      const question = this._layoutConfig.getQuestion('AvailableSizes');

      if (question?.value === 'Recommended') {
        const answer = question.answers.find((a) => a.name === question.value);
        this.footerProductType = `${answer?.text ?? 'Optimized'} Fan(s)`;
      } else if (this.footerProductType !== 'Fan(s)') {
        this.footerProductType = 'Fan(s)';
      }
    }
  };
}
